// Typed readers for the AI SDK UI message parts the Copilot renders.
//
// The SDK types a message part as a union that widens with every tool name
// (`tool-<name>`, `dynamic-tool`, plus the legacy `tool-invocation` shape), so
// the drawer reads the parts through these helpers instead of casting to `any`.
//
// `hasRenderableAnswer` is the contract behind the "no answer" state: a run can
// finish with `start` / `start-step` / `finish-step` / `finish` and no content
// part at all, and the drawer must say so instead of showing an empty bubble.

import { knowledgeOf } from "./knowledge-parts";

export type MessageLike = {
  id?: string;
  role?: string;
  parts?: readonly unknown[];
  /** Present on a loaded conversation, absent while a turn streams. */
  createdAt?: Date | string;
};

export type ToolPartView = {
  toolCallId: string;
  toolName: string;
  state: string;
  input: unknown;
  output: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function partType(part: unknown): string | null {
  const record = asRecord(part);
  return record ? readString(record.type) : null;
}

export function isToolPart(part: unknown): boolean {
  const type = partType(part);
  if (type === null) return false;
  return type === "dynamic-tool" || type.startsWith("tool-");
}

/** Normalizes both the v6 (`tool-<name>` / `dynamic-tool`) and the legacy
 * (`tool-invocation`) shapes into one view. Returns null for non-tool parts. */
export function toolPartView(part: unknown): ToolPartView | null {
  const record = asRecord(part);
  const type = record ? readString(record.type) : null;
  if (!record || type === null || !isToolPart(record)) return null;

  if (type === "tool-invocation") {
    const invocation = asRecord(record.toolInvocation) ?? record;
    return {
      toolCallId: readString(invocation.toolCallId) ?? readString(record.toolCallId) ?? "",
      toolName: readString(invocation.toolName) ?? readString(record.toolName) ?? "",
      state: readString(invocation.state) ?? readString(record.state) ?? "",
      input: invocation.args ?? record.args ?? null,
      output: invocation.result ?? record.result ?? null,
    };
  }

  const toolName =
    readString(record.toolName) ??
    (type === "dynamic-tool" ? "" : type.slice("tool-".length));

  return {
    toolCallId: readString(record.toolCallId) ?? "",
    toolName,
    state: readString(record.state) ?? "",
    input: record.input ?? null,
    output: record.output ?? null,
  };
}

export type FilePartView = {
  filename: string;
  mediaType: string;
  url: string;
};

/** The attachments the clinic sent with a turn. The `url` is the inline data
 * URL the turn carried; it is rendered as a thumbnail and never stored. */
export function filePartsOf(message: MessageLike | null | undefined): FilePartView[] {
  return partsOf(message)
    .map((part) => {
      const record = asRecord(part);
      if (!record || readString(record.type) !== "file") return null;
      return {
        filename: readString(record.filename) ?? "arquivo",
        mediaType: readString(record.mediaType) ?? "",
        url: readString(record.url) ?? "",
      };
    })
    .filter((view): view is FilePartView => view !== null);
}

export function isToolResultState(state: string): boolean {
  return state === "result" || state === "output-available";
}

export function isToolRunningState(state: string): boolean {
  return state === "call" || state === "input-available" || state === "input-streaming";
}

export function textOfPart(part: unknown): string {
  const record = asRecord(part);
  if (!record || readString(record.type) !== "text") return "";
  return readString(record.text) ?? "";
}

export function partsOf(message: MessageLike | null | undefined): readonly unknown[] {
  return message?.parts ?? [];
}

export function messageText(message: MessageLike | null | undefined): string {
  return partsOf(message).map(textOfPart).join("");
}

export function toolPartsOf(message: MessageLike | null | undefined): ToolPartView[] {
  return partsOf(message)
    .map(toolPartView)
    .filter((view): view is ToolPartView => view !== null);
}

export function hasToolResult(message: MessageLike | null | undefined): boolean {
  return toolPartsOf(message).some((tool) => isToolResultState(tool.state));
}

/** True when the message carries something the drawer can show: text, or a
 * tool call. `step-start` and other control parts do not count. */
/** The reason code of a `data-paused` part, when the turn ended paused. */
export function pausedReasonOf(message: MessageLike | null | undefined): string | null {
  for (const part of partsOf(message)) {
    if ((part as { type?: string }).type !== "data-paused") continue;
    const reason = (part as { data?: { reason_code?: unknown } }).data?.reason_code;
    if (typeof reason === "string" && reason) return reason;
  }
  return null;
}

/** The ref types of a `data-partial-context` part, when the turn answered
 * without part of the page context. `null` when the turn carried no such part;
 * an array (possibly empty) when it did. The frame carries no reason code
 * (SCRUM-306, Decision 14), so there is nothing else to read. */
export function partialContextRefTypesOf(
  message: MessageLike | null | undefined,
): string[] | null {
  for (const part of partsOf(message)) {
    if ((part as { type?: string }).type !== "data-partial-context") continue;
    const refTypes = (part as { data?: { ref_types?: unknown } }).data?.ref_types;
    return Array.isArray(refTypes)
      ? refTypes.filter((type): type is string => typeof type === "string")
      : [];
  }
  return null;
}

/**
 * [codex2-14] A PAUSED turn is an answer. This counted only text and tool parts,
 * so a message whose only part is `data-paused` would render the notice AND the
 * drawer's empty-answer state — two contradictory statements about one turn.
 */
export function hasRenderableAnswer(message: MessageLike | null | undefined): boolean {
  if (!message) return false;
  const hasText = partsOf(message).some((part) => textOfPart(part).trim().length > 0);
  return (
    hasText ||
    toolPartsOf(message).length > 0 ||
    pausedReasonOf(message) !== null ||
    // SCRUM-306: so is a turn that answered with partial page context.
    partialContextRefTypesOf(message) !== null ||
    knowledgeOf(message)?.status === "unavailable"
  );
}

const TIME_FORMAT = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});

/** The message clock, when the message carries one (loaded conversations do;
 * a message still streaming does not). */
export function formatMessageTime(message: MessageLike | null | undefined): string | null {
  const raw = asRecord(message)?.createdAt;
  const date =
    raw instanceof Date ? raw : typeof raw === "string" ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return TIME_FORMAT.format(date);
}

const DAY_FORMAT = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
});

/** The message clock as a date, when the message carries one. */
export function messageDate(message: MessageLike | null | undefined): Date | null {
  const raw = asRecord(message)?.createdAt;
  const date =
    raw instanceof Date ? raw : typeof raw === "string" ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Hoje", "Ontem" or the day itself — the separator the thread shows when a
 * conversation crosses midnight. */
export function formatDayLabel(date: Date, now: Date = new Date()): string {
  if (isSameDay(date, now)) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Ontem";
  return DAY_FORMAT.format(date);
}
